import * as React from 'react';
import {useEffect, useState} from 'react';
import {Filter, FilterType} from '../CommonInterfaces';
import DropDown, {DropDownOption} from '../DropDown';
import {BaseFilterProps} from './BaseFilterInterfaces';
import FilterInputRenderer from './FilterInputRenderer';

const BaseFilter = (props: BaseFilterProps) => {
  const {
    filters,
    allowedFilterKeys,
    filterMap,
    activeFiltersAdapter,
    onFilterChange,
    onFilterOptionsFetch,
    getFilterDisplayValue,
    getFilterSearchType,
    isFilterAvailable,
    parentFilterKey = 'parent_filter',
    parentFilterDisplayName = 'Select Filter',
    loading = false
  } = props;

  const [filterValue, setFilterValue] = useState('');
  const [applyFilter, setApplyFilter] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const parentFilter = filters.find((f) => f.key === parentFilterKey) || {
    displayName: parentFilterDisplayName,
    key: parentFilterKey,
    type: FilterType.Dropdown,
    options: []
  };
  const availableFilters = filters.filter(
    (f) => f.key === parentFilterKey || (allowedFilterKeys.includes(f.key) && (isFilterAvailable?.(f) ?? true))
  );
  parentFilter.options = availableFilters.map((f) => ({displayName: f.displayName, value: f.key}));

  const [selectedFilter, setSelectedFilter] = useState(parentFilter);

  useEffect(() => {
    if (onFilterOptionsFetch && selectedFilter?.key !== parentFilterKey) {
      onFilterOptionsFetch(selectedFilter.key).then((options) => {
        if (options) setSelectedFilter({...selectedFilter, options});
      });
    }
  }, [selectedFilter?.key, onFilterOptionsFetch, parentFilterKey]);

  useEffect(() => {
    if (!applyFilter) return;
    if (selectedFilter.key === parentFilterKey) {
      const childFilter = filters.find((f) => f.key === filterValue);
      if (childFilter) {
        setSelectedFilter(childFilter);
        setFilterValue(''); // Clear filterValue when selecting a child filter
      }
    } else if (selectedFilter.type === FilterType.Range) {
      if (startDate.trim()) onFilterChange(selectedFilter.key, startDate.trim());
      if (endDate.trim()) onFilterChange(selectedFilter.key, endDate.trim());
      setSelectedFilter(parentFilter);
      setFilterValue('');
      setStartDate('');
      setEndDate('');
    } else if (filterValue.trim()) {
      if (selectedFilter.allowMultiple) {
        const existing = activeFiltersAdapter.getAll(selectedFilter.key);
        if (!existing.includes(filterValue)) {
          onFilterChange(selectedFilter.key, [...existing, filterValue]);
        }
      } else {
        onFilterChange(selectedFilter.key, filterValue.trim());
      }
      setSelectedFilter(parentFilter);
      setFilterValue('');
    }
    setApplyFilter(false);
  }, [
    applyFilter,
    filterValue,
    selectedFilter,
    startDate,
    endDate,
    onFilterChange,
    activeFiltersAdapter,
    parentFilter,
    parentFilterKey,
    filters
  ]);

  const resetFilterInput = () => {
    setSelectedFilter(parentFilter);
    setFilterValue('');
    setStartDate('');
    setEndDate('');
  };

  const handleChangeAndApplyFilter = (option: DropDownOption) => {
    setFilterValue(`${option.value}`);
    setApplyFilter(true);
  };

  const handleFilterValueChange = (e: React.ChangeEvent<HTMLInputElement>) => setFilterValue(e.target.value);

  const handleInputKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') setApplyFilter(true);
  };

  const handleTypeAheadChangeAndApplyFilter = (value: string) => {
    setFilterValue(value);
    setApplyFilter(true);
  };

  const handleTypeAheadFilterChange = (query: string) => setFilterValue(query);

  const handleChangeAndApplyRadioFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterValue(e.target.value);
    setApplyFilter(true);
  };

  const removeFilter = (filterKey: string, valueToRemove: string) => {
    const filter = filters.find((f) => f.key === filterKey);
    if (filter?.allowMultiple) {
      const existing = activeFiltersAdapter.getAll(filterKey);
      onFilterChange(
        filterKey,
        existing.filter((v) => v !== valueToRemove)
      );
    } else {
      onFilterChange(filterKey, '');
    }
  };

  const getFilterSearchTypeDefault = (filter: Filter): string => getFilterSearchType?.(filter) || 'Is';

  const activeFilters: JSX.Element[] = [];
  activeFiltersAdapter.forEach((value, key) => {
    const filter = filters.find((f) => f.key === key);
    if (!filter) return;
    const displayValue = getFilterDisplayValue?.(key, value) || value;
    activeFilters.push(
      <li key={`${key}-${value}`}>
        <div className="active-filter" data-testid="active-filter-pill">
          <span className="active-filter-label">
            <strong>{filter.displayName}:</strong> {displayValue}
          </span>
          <span className="active-filter-action" data-testid="remove-filter" onClick={() => removeFilter(key, value)}>
            x
          </span>
        </div>
      </li>
    );
  });

  const label = selectedFilter?.key !== parentFilterKey ? `Filter by ${selectedFilter.displayName}:` : 'Filter by:';
  const addFilter = (
    <li>
      <ul className="list-inline">
        <li>{label}</li>
        <FilterInputRenderer
          selectedFilter={selectedFilter}
          filterValue={filterValue}
          startDate={startDate}
          endDate={endDate}
          parentFilterKey={parentFilterKey}
          onFilterValueChange={handleFilterValueChange}
          onInputKeyUp={handleInputKeyUp}
          onChangeAndApplyFilter={handleChangeAndApplyFilter}
          onTypeAheadChangeAndApply={handleTypeAheadChangeAndApplyFilter}
          onTypeAheadFilterChange={handleTypeAheadFilterChange}
          onChangeAndApplyRadio={handleChangeAndApplyRadioFilter}
          onStartDateChange={(e) => setStartDate(e.target.value)}
          onEndDateChange={(e) => setEndDate(e.target.value)}
          onApplyFilter={() => setApplyFilter(true)}
          onResetFilter={resetFilterInput}
          getFilterSearchType={getFilterSearchTypeDefault}
        />
      </ul>
    </li>
  );

  return (
    <ul className="list-inline list-inline-overflow">
      {addFilter}
      {activeFilters.length > 0 && (
        <li className="vertical-rule" data-testid="vertical-rule">
          &nbsp;
        </li>
      )}
      {activeFilters}
    </ul>
  );
};

export default BaseFilter;
